import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Image, Loader2, Upload, Sparkles, ArrowLeft, ArrowRight, X, RefreshCw, ChevronDown, Pencil, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { motion, useReducedMotion } from 'framer-motion';
import OrbisSpinner from '@/components/ui/orbis-spinner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Image Preview Lightbox ── */
const ImageLightbox = ({ src, alt, dimensions, onClose }) => {
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)' }}
      onClick={onClose}
    >
      <div className="relative max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/60 hover:text-white transition-colors cursor-pointer"
        >
          <X className="w-6 h-6" />
        </button>
        <img src={src} alt={alt} className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg" />
        <div
          className="absolute bottom-0 left-0 right-0 p-4 flex items-center justify-between"
          style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', borderRadius: '0 0 8px 8px' }}
        >
          <span style={{ fontSize: 14, color: 'var(--orbis-t2)' }}>{alt}</span>
          {dimensions && (
            <span style={{ fontSize: 12, fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t4)' }}>{dimensions}</span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Image Slot Card ── */
const ImageSlotCard = ({ slot, projectId, onSlotUpdate, onSlotClear, onPreview, externalGenerating }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState(slot.style_guidance || '');
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const fileInputRef = useRef(null);

  // Sync local prompt when slot.style_guidance changes externally
  useEffect(() => {
    if (!promptOpen) setEditedPrompt(slot.style_guidance || '');
  }, [slot.style_guidance, promptOpen]);

  const imageUrl = slot.placeholder_url
    ? slot.placeholder_url.startsWith('/static')
      ? `${BACKEND_URL}${slot.placeholder_url}`
      : slot.placeholder_url
    : null;

  const isDefault = imageUrl && imageUrl.includes('placehold.co');
  const hasImage = imageUrl && !isDefault;
  const isPollinations = hasImage && imageUrl.includes('pollinations.ai');
  const promptModified = editedPrompt.trim() !== (slot.style_guidance || '').trim();

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Maximum 5MB.'); e.target.value = ''; return; }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/image-slots/${slot.id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSlotUpdate(slot.id, response.data.placeholder_url);
    } catch (error) {
      toast.error(error.response?.status === 413 ? 'File too large.' : 'Upload failed.');
    } finally { setIsUploading(false); e.target.value = ''; }
  };

  const handleGenerate = async (regenerate = false) => {
    setIsGenerating(true);
    try {
      const payload = { regenerate };
      // If user edited the prompt, send it as override
      if (promptModified && editedPrompt.trim()) {
        payload.prompt_override = editedPrompt.trim();
      }
      const response = await axios.post(
        `${API_URL}/magic/projects/${projectId}/image-slots/${slot.id}/generate`,
        payload,
        { timeout: 90000 }
      );
      // Update both URL and style_guidance from response
      const extraFields = response.data.style_guidance ? { style_guidance: response.data.style_guidance } : {};
      onSlotUpdate(slot.id, response.data.placeholder_url, extraFields);
      if (response.data.is_fallback) {
        toast.warning('Fireworks API unavailable — stock photo used. Try regenerating.');
      } else {
        toast.success(regenerate ? 'New image generated!' : 'Image generated!');
      }
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Insufficient credits — upgrade your plan to generate more images.');
      } else {
        toast.error(error.code === 'ECONNABORTED' ? 'Image generation timed out — try again.' : 'Failed to generate image.');
      }
    } finally { setIsGenerating(false); }
  };

  const handleSavePrompt = async () => {
    if (!promptModified) return;
    setIsSavingPrompt(true);
    try {
      await axios.post(`${API_URL}/magic/projects/${projectId}/image-slots/${slot.id}/update-prompt`, {
        style_guidance: editedPrompt.trim(),
      });
      onSlotUpdate(slot.id, slot.placeholder_url, { style_guidance: editedPrompt.trim() });
      toast.success('Prompt saved');
    } catch {
      toast.error('Failed to save prompt');
    } finally { setIsSavingPrompt(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const dt = new DataTransfer();
      dt.items.add(file);
      if (fileInputRef.current) {
        fileInputRef.current.files = dt.files;
        fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  const isBusy = isUploading || isGenerating || externalGenerating;
  const slotLabel = slot.section_name || slot.description || slot.section_type || 'Untitled';
  const dimensions = slot.dimensions || '';

  // Prompt source badge: if slot has generation_metadata, content came from Content AI
  const promptSource = slot.generation_metadata ? 'Content AI' : (slot.style_guidance ? 'Auto' : null);

  return (
    <div
      style={{
        background: 'var(--orbis-s1)',
        border: '1px solid var(--orbis-s2)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {/* Header — slot name + dimensions + prompt toggle */}
      <div
        className="flex items-center justify-between"
        style={{ padding: '10px 14px', borderBottom: '1px solid var(--orbis-s2)' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span style={{ fontSize: 10, fontFamily: 'var(--orbis-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--orbis-t4)' }}>
            {slotLabel}
          </span>
          {promptSource && (
            <span style={{
              fontSize: 9, fontFamily: 'var(--orbis-mono)',
              color: promptSource === 'Content AI' ? 'var(--orbis-blue)' : 'var(--orbis-t4)',
              background: promptSource === 'Content AI' ? 'rgba(184,204,223,0.10)' : 'var(--orbis-s2)',
              border: `1px solid ${promptSource === 'Content AI' ? 'rgba(184,204,223,0.20)' : 'var(--orbis-s3)'}`,
              borderRadius: 3, padding: '1px 5px',
              whiteSpace: 'nowrap',
            }}>
              {promptSource}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {dimensions && (
            <span style={{ fontSize: 10, fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t4)' }}>
              {dimensions}
            </span>
          )}
          <button
            onClick={() => setPromptOpen(!promptOpen)}
            className="flex items-center gap-1 transition-colors hover:text-[var(--orbis-t2)]"
            style={{ fontSize: 10, color: 'var(--orbis-t4)', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            title="Edit prompt"
          >
            <Pencil className="w-3 h-3" />
            <ChevronDown className="w-3 h-3 transition-transform" style={{ transform: promptOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </button>
        </div>
      </div>

      {/* Collapsible prompt editor */}
      {promptOpen && (
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--orbis-s2)', background: 'var(--orbis-bg)' }}>
          <textarea
            value={editedPrompt}
            onChange={(e) => setEditedPrompt(e.target.value)}
            placeholder="Describe the image you want..."
            rows={3}
            style={{
              width: '100%', resize: 'vertical', fontSize: 12, lineHeight: 1.5,
              fontFamily: 'var(--orbis-font)', color: 'var(--orbis-t2)',
              background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)',
              borderRadius: 8, padding: '8px 10px', outline: 'none',
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--orbis-s3)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'var(--orbis-s2)'; }}
          />
          <div className="flex items-center justify-between" style={{ marginTop: 6 }}>
            <span style={{ fontSize: 10, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)' }}>
              {promptModified ? 'Modified' : 'Original prompt'}
            </span>
            <div className="flex items-center gap-2">
              {promptModified && (
                <button
                  onClick={handleSavePrompt}
                  disabled={isSavingPrompt}
                  style={{
                    fontSize: 11, color: 'var(--orbis-t3)', background: 'var(--orbis-s2)',
                    border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                    fontFamily: 'var(--orbis-font)',
                  }}
                >
                  {isSavingPrompt ? 'Saving...' : 'Save'}
                </button>
              )}
              <button
                onClick={() => { handleGenerate(hasImage); setPromptOpen(false); }}
                disabled={isBusy}
                style={{
                  fontSize: 11, fontWeight: 500, fontFamily: 'var(--orbis-font)',
                  color: 'var(--orbis-t1)', background: 'var(--orbis-s3)',
                  border: 'none', borderRadius: 6, padding: '4px 12px',
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  opacity: isBusy ? 0.5 : 1,
                }}
              >
                <span className="flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" style={{ color: 'var(--orbis-blue)' }} />
                  {hasImage ? 'Regenerate' : 'Generate'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dropzone */}
      <div
        style={{
          height: 200,
          background: 'var(--orbis-bg)',
          border: isDragOver ? '1px dashed var(--orbis-s3)' : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', overflow: 'hidden',
          cursor: hasImage ? 'pointer' : 'default',
          transition: 'border-color 150ms ease',
        }}
        onClick={() => hasImage && onPreview(imageUrl, slot.description, dimensions)}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        {hasImage ? (
          <>
            <img src={imageUrl} alt={slot.description} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            {/* Fallback warning badge */}
            {isPollinations && (
              <div
                className="absolute top-2 left-2 flex items-center gap-1"
                style={{
                  background: 'rgba(234,179,8,0.15)',
                  border: '1px solid rgba(234,179,8,0.35)',
                  borderRadius: 4,
                  padding: '2px 7px',
                  pointerEvents: 'none',
                }}
              >
                <span style={{ fontSize: 10, color: '#EAB308', fontFamily: 'var(--orbis-mono)' }}>
                  ⚠ Stock fallback
                </span>
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
              <span style={{ fontSize: 13, color: 'var(--orbis-t1)', background: 'rgba(0,0,0,0.6)', padding: '5px 12px', borderRadius: 6 }}>
                {isPollinations ? 'Regenerate with AI' : 'Replace'}
              </span>
            </div>
          </>
        ) : isBusy ? (
          <div className="flex flex-col items-center gap-2">
            <OrbisSpinner size={28} />
            <span style={{ fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>
              {isGenerating || externalGenerating ? 'Generating...' : 'Uploading...'}
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Image className="w-5 h-5" style={{ color: 'var(--orbis-t4)' }} />
            <span style={{ fontSize: 12, color: 'var(--orbis-t4)' }}>Drop image or generate</span>
          </div>
        )}
      </div>

      {/* Footer — action buttons */}
      <div
        className="flex items-center"
        style={{ borderTop: '1px solid var(--orbis-s2)' }}
      >
        {hasImage ? (
          <>
            <button
              onClick={() => handleGenerate(true)}
              disabled={isBusy}
              className="flex items-center gap-1.5 justify-center flex-1 transition-colors hover:bg-[var(--orbis-s2)]"
              style={{
                height: 38, fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)',
                cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.5 : 1,
                borderRight: '1px solid var(--orbis-s2)',
              }}
            >
              <RefreshCw className="w-3 h-3" />
              Regenerate
            </button>
            <button
              onClick={handleUploadClick}
              disabled={isBusy}
              className="flex items-center gap-1.5 justify-center flex-1 transition-colors hover:bg-[var(--orbis-s2)]"
              style={{
                height: 38, fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)',
                cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.5 : 1,
                borderRight: '1px solid var(--orbis-s2)',
              }}
            >
              <Upload className="w-3 h-3" />
              Replace
            </button>
            <button
              onClick={() => onSlotClear()}
              className="flex items-center justify-center transition-colors hover:text-red-400"
              style={{ height: 38, width: 50, color: 'var(--orbis-t4)', fontSize: 11, fontFamily: 'var(--orbis-font)' }}
            >
              Clear
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleUploadClick}
              disabled={isBusy}
              className="flex items-center gap-1.5 justify-center flex-1 transition-colors hover:bg-[var(--orbis-s2)]"
              style={{
                height: 38, fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)',
                cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.5 : 1,
                borderRight: '1px solid var(--orbis-s2)',
              }}
            >
              <Upload className="w-3 h-3" />
              Upload
            </button>
            <button
              onClick={() => handleGenerate(false)}
              disabled={isBusy}
              className="flex items-center gap-1.5 justify-center flex-1 transition-colors hover:bg-[var(--orbis-s2)]"
              style={{
                height: 38, fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)',
                cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.5 : 1,
              }}
            >
              <Sparkles className="w-3 h-3" style={{ color: 'var(--orbis-blue)' }} />
              Generate
            </button>
          </>
        )}
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
    </div>
  );
};

/* ── Shimmer ghost card for empty state ── */
const GhostCard = () => (
  <div style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--orbis-s2)' }}>
      <div className="shimmer-bar" style={{ width: '60%', height: 10, borderRadius: 4 }} />
    </div>
    <div style={{ height: 200, background: 'var(--orbis-bg)', position: 'relative', overflow: 'hidden' }}>
      <div
        className="shimmer-scan"
        style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.03) 50%, transparent 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmerScan 2s ease-in-out infinite',
        }}
      />
    </div>
    <div className="flex" style={{ borderTop: '1px solid var(--orbis-s2)' }}>
      <div className="shimmer-bar flex-1" style={{ height: 38 }} />
      <div className="shimmer-bar flex-1" style={{ height: 38, borderLeft: '1px solid var(--orbis-s2)' }} />
    </div>
    <style>{`
      .shimmer-bar {
        background: linear-gradient(90deg, var(--orbis-s1) 0%, var(--orbis-s2) 50%, var(--orbis-s1) 100%);
        background-size: 200% 100%;
        animation: shimmerScan 2s ease-in-out infinite;
      }
      @keyframes shimmerScan {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  </div>
);

/* ── Helper: group slots by section ── */
function groupSlotsBySections(slots) {
  const sectionMap = new Map();
  for (const slot of slots) {
    // Group key: use section_type (machine type) for consistent grouping
    // Display name: section_type formatted (e.g. "feature_grid" → "Feature Grid")
    const groupKey = slot.section_type || slot.description?.split(' — ')[0]?.split(' - ')[0] || 'Other';
    if (!sectionMap.has(groupKey)) {
      sectionMap.set(groupKey, { slots: [], displayName: groupKey });
    }
    sectionMap.get(groupKey).slots.push(slot);
  }
  return Array.from(sectionMap.entries()).map(([key, { slots: sectionSlots, displayName }]) => ({
    name: displayName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    slots: sectionSlots,
    filledCount: sectionSlots.filter(s => s.placeholder_url && !s.placeholder_url.includes('placehold.co')).length,
    totalCount: sectionSlots.length,
  }));
}

/* ── Main Component ── */
export default function AssetsStage({ projectId, imageSlots, onContinue, isProcessing, onSlotUpdate }) {
  const [isBuilding, setIsBuilding] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, src: '', alt: '', dimensions: '' });
  const [activeSection, setActiveSection] = useState(null);
  const [autoGenerating, setAutoGenerating] = useState(new Set()); // slot IDs currently being auto-generated
  const cardRefs = useRef({});
  const prefersReducedMotion = useReducedMotion();

  const slots = imageSlots || [];
  const sections = useMemo(() => groupSlotsBySections(slots), [slots]);

  const filledCount = slots.filter(s => s.placeholder_url && !s.placeholder_url.includes('placehold.co')).length;
  const fallbackCount = slots.filter(s => s.placeholder_url && s.placeholder_url.includes('pollinations.ai')).length;
  const isAutoGenerating = autoGenerating.size > 0;

  // Set initial active section
  useEffect(() => {
    if (sections.length > 0 && !activeSection) {
      setActiveSection(sections[0].name);
    }
  }, [sections, activeSection]);

  // Get slots for current active section
  const activeSectionData = sections.find(s => s.name === activeSection);
  const activeSectionSlots = activeSectionData?.slots || [];

  const handleContinue = async () => {
    setIsBuilding(true);
    try { await onContinue(); }
    finally { setIsBuilding(false); }
  };

  const openPreview = (src, alt, dimensions) => {
    setLightbox({ open: true, src, alt, dimensions });
  };

  // Auto Generate All — generate images for all empty slots in parallel batches
  const handleAutoGenerateAll = async () => {
    const emptySlots = slots.filter(s => !s.placeholder_url || s.placeholder_url.includes('placehold.co'));
    if (emptySlots.length === 0) {
      toast.info('All image slots are already filled.');
      return;
    }

    // Mark all empty slots as generating
    setAutoGenerating(new Set(emptySlots.map(s => s.id)));
    toast.success(`Generating ${emptySlots.length} images...`);

    let fallbackCount = 0;
    let errorCount = 0;

    // Process in batches of 3 to avoid overwhelming the API
    const BATCH_SIZE = 3;
    for (let i = 0; i < emptySlots.length; i += BATCH_SIZE) {
      const batch = emptySlots.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(async (slot) => {
          try {
            const response = await axios.post(
              `${API_URL}/magic/projects/${projectId}/image-slots/${slot.id}/generate`,
              { regenerate: false },
              { timeout: 90000 }
            );
            onSlotUpdate(slot.id, response.data.placeholder_url);
            if (response.data.is_fallback) fallbackCount++;
          } finally {
            setAutoGenerating(prev => {
              const next = new Set(prev);
              next.delete(slot.id);
              return next;
            });
          }
        })
      );
      errorCount += results.filter(r => r.status === 'rejected').length;
    }

    if (fallbackCount > 0) {
      toast.warning(`${fallbackCount} image${fallbackCount > 1 ? 's' : ''} used stock fallbacks — click Regenerate on the ⚠ marked cards.`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} image${errorCount > 1 ? 's' : ''} failed to generate.`);
    }
  };

  const hasSlots = slots.length > 0;

  return (
    <div className="flex flex-col w-full" style={{ height: 'calc(100vh - 64px)', background: 'var(--surface-0)' }}>
      {/* Lightbox */}
      {lightbox.open && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          dimensions={lightbox.dimensions}
          onClose={() => setLightbox({ open: false, src: '', alt: '', dimensions: '' })}
        />
      )}

      <div className="flex flex-1 min-h-0">
        {/* ── LEFT PANEL — Section Navigator (Orbis.pencil design) ── */}
        <div
          className="shrink-0 flex flex-col"
          style={{ width: 480, borderRight: '1px solid #27272A60', background: 'var(--orbis-bg)' }}
        >
          {/* Section heading */}
          <div style={{ padding: '20px 20px 12px' }}>
            <span style={{
              fontSize: 11, fontFamily: 'var(--orbis-mono)', letterSpacing: '0.12em',
              textTransform: 'uppercase', color: 'var(--orbis-t4)',
            }}>
              Sections
            </span>
          </div>

          {/* Section list */}
          <ScrollArea className="flex-1 min-h-0">
            <div style={{ padding: '0 12px' }}>
              {sections.map((section) => {
                const isActive = activeSection === section.name;
                const isFilled = section.filledCount === section.totalCount;
                const hasAny = section.filledCount > 0;

                return (
                  <button
                    key={section.name}
                    onClick={() => setActiveSection(section.name)}
                    className="w-full text-left flex items-center gap-3 transition-all"
                    style={{
                      padding: '14px 16px',
                      borderRadius: 10,
                      background: isActive ? 'var(--orbis-s1)' : 'transparent',
                      marginBottom: 4,
                      cursor: 'pointer',
                    }}
                  >
                    {/* Status dot */}
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: isFilled ? 'var(--orbis-positive)' : hasAny ? 'var(--orbis-positive)' : 'transparent',
                      border: isFilled || hasAny ? 'none' : '1.5px solid var(--orbis-s3)',
                      opacity: isFilled ? 1 : hasAny ? 0.5 : 1,
                    }} />
                    {/* Section name */}
                    <span style={{
                      fontSize: 15, fontWeight: isActive ? 500 : 400,
                      color: isActive ? 'var(--orbis-t1)' : hasAny ? 'var(--orbis-t3)' : 'var(--orbis-t4)',
                      fontFamily: 'var(--orbis-font)',
                      transition: 'color 150ms ease',
                    }}>
                      {section.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </ScrollArea>

          {/* Progress footer */}
          <div style={{ padding: '16px 20px 20px', borderTop: '1px solid var(--orbis-s2)' }}>
            <div className="flex items-center gap-2">
              <span style={{ fontSize: 13, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>
                {filledCount} of {slots.length} filled
              </span>
              {fallbackCount > 0 && (
                <span style={{
                  fontSize: 10, color: '#EAB308',
                  background: 'rgba(234,179,8,0.10)',
                  border: '1px solid rgba(234,179,8,0.25)',
                  borderRadius: 4, padding: '1px 6px',
                  fontFamily: 'var(--orbis-mono)',
                }}>
                  {fallbackCount} fallback{fallbackCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div style={{
              marginTop: 8, height: 2, background: 'var(--orbis-s2)',
              borderRadius: 1, overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: slots.length > 0 ? `${(filledCount / slots.length) * 100}%` : '0%',
                background: 'var(--orbis-blue)',
                borderRadius: 1,
                transition: 'width 300ms ease',
              }} />
            </div>
          </div>
        </div>

        {/* ── RIGHT ZONE — Asset Grid ── */}
        <div className="flex-1 flex flex-col min-w-0">
          <ScrollArea className="flex-1 min-h-0">
            <div style={{ padding: 20 }}>
              {/* Section header */}
              <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>
                  {activeSection || 'Image Assets'} — {activeSectionSlots.length} image slot{activeSectionSlots.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={handleAutoGenerateAll}
                  disabled={isAutoGenerating}
                  className="flex items-center gap-2 rounded-full transition-colors hover:bg-[var(--orbis-s2)]"
                  style={{
                    padding: '7px 16px', fontSize: 12, fontWeight: 500,
                    color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)',
                    background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)',
                    cursor: isAutoGenerating ? 'not-allowed' : 'pointer',
                    opacity: isAutoGenerating ? 0.6 : 1,
                  }}
                >
                  {isAutoGenerating ? (
                    <>
                      <OrbisSpinner size={14} />
                      Generating {autoGenerating.size} remaining...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--orbis-blue)' }} />
                      Auto Generate All
                    </>
                  )}
                </button>
              </div>

              {/* Grid */}
              {hasSlots ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {activeSectionSlots.map((slot, i) => (
                    <motion.div
                      key={slot.id}
                      ref={el => { cardRefs.current[slot.id] = el; }}
                      initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: prefersReducedMotion ? 0 : i * 0.06 }}
                    >
                      <ImageSlotCard
                        slot={slot}
                        projectId={projectId}
                        onSlotUpdate={onSlotUpdate}
                        onSlotClear={() => onSlotUpdate(slot.id, null)}
                        onPreview={openPreview}
                        externalGenerating={autoGenerating.has(slot.id)}
                      />
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center" style={{ minHeight: 400 }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 w-full mb-10 opacity-50 pointer-events-none">
                    <GhostCard />
                    <GhostCard />
                    <GhostCard />
                  </div>
                  <p style={{ fontSize: 14, color: 'var(--orbis-t4)', textAlign: 'center', marginBottom: 8 }}>
                    No image assets defined yet — complete the Structure step first
                  </p>
                  <button
                    onClick={() => window.history.back()}
                    className="transition-colors"
                    style={{ fontSize: 13, color: 'var(--orbis-t3)', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    &larr; Back to Structure
                  </button>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div className="magic-bottom-bar">
        <button
          onClick={() => window.history.back()}
          className="flex items-center justify-center gap-2 transition-colors rounded-full"
          style={{ height: 44, padding: '0 24px', background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)', fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Previous
        </button>
        <div className="flex items-center gap-3">
          <span style={{ fontSize: 11, color: 'var(--orbis-s3)', fontFamily: 'var(--orbis-font)', letterSpacing: '0.5px' }}>
            Assets · Step 4 of 7
          </span>
          {slots.length > 0 && filledCount < slots.length && (
            <span
              className="flex items-center gap-1.5"
              style={{
                fontSize: 11, fontFamily: 'var(--orbis-mono)',
                color: '#EAB308',
                background: 'rgba(234,179,8,0.08)',
                border: '1px solid rgba(234,179,8,0.20)',
                borderRadius: 6,
                padding: '4px 10px',
              }}
            >
              <AlertTriangle className="w-3 h-3" />
              {slots.length - filledCount} slot{slots.length - filledCount !== 1 ? 's' : ''} will be auto-generated during build
            </span>
          )}
        </div>
        <div className="orbis-cta-ring rounded-full p-0.5">
          <button
            onClick={handleContinue}
            disabled={isBuilding}
            className="orbis-cta flex items-center justify-center gap-2 transition-all rounded-full"
            style={{ height: 44, padding: '0 28px', opacity: isBuilding ? 0.4 : 1, fontSize: 12, fontWeight: 500, cursor: isBuilding ? 'not-allowed' : 'pointer', fontFamily: 'var(--orbis-font)' }}
            data-testid="build-btn"
          >
            {isBuilding ? <><Loader2 className="w-4 h-4 animate-spin" />Building...</> : <>Build Website <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
